import React from 'react'
import { Box, Container, Typography } from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { LenderDetails } from '../../../ApplicationFormContext'
import { sanitizeLenderName } from '../../../../../utils/utils'
import { LENDER_ICONS } from '../../../../Introductions/PrefillIntroduction/PrefillIntroductionV3/PrefillIntroductionV3'
import { formlessColors } from '@zuto/theme/src/mui/Colors'

interface DynamicLenderDetailsBannerProps {
  lenderDetails?: LenderDetails
  source: string
}

const HERO_BG = 'https://cdn.zuto.cloud/assets/images/apply/desktop/cs-hero-banner.avif'

const getLenderLogo = (rawName?: string) => {
  if (!rawName) return null

  const directKey = rawName.toLowerCase().trim()
  const sanitizedKey = sanitizeLenderName(rawName).toLowerCase().trim()

  return LENDER_ICONS[directKey] ?? LENDER_ICONS[sanitizedKey] ?? null
}

const renderLenderInline = (lenderDetails?: LenderDetails) => {
  if (!lenderDetails?.lenderName) return null

  const { lenderName } = lenderDetails
  const logo = getLenderLogo(lenderName)
  const displayName = sanitizeLenderName(lenderName)

  if (logo) {
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          ml: '4px',
          position: 'relative'
        }}
      >
        {logo}
        <Typography
          component="span"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            p: 0,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            border: 0
          }}
        >
          {displayName}
        </Typography>
      </Box>
    )
  }

  return (
    <Typography component="span" variant="body1" sx={{ fontWeight: 600, ml: '4px' }}>
      {displayName}
    </Typography>
  )
}

const formatAprValue = (apr?: number): string | null => {
  if (apr == null) return null
  return `${apr.toFixed(1)}%`
}

const getHeadingText = (source: string, preapproved?: boolean): string => {
  const normalizedSource = source.toLowerCase().trim()

  const preapprovedDefault = "Great News! You're Pre-Approved For Car Finance"
  const nonPreapprovedDefault = "Great News, We've Got Your Application!"

  const headingTextBySource = {
    carmoola: {
      preapproved: preapprovedDefault,
      nonPreapproved: "Welcome To Zuto, Partner Of Carmoola!"
    },
    // clearscore, experian
    default: {
      preapproved: preapprovedDefault,
      nonPreapproved: nonPreapprovedDefault
    }
  } as const

  const sourceCopy = headingTextBySource[normalizedSource as keyof typeof headingTextBySource] ?? headingTextBySource.default

  return preapproved ? sourceCopy.preapproved : sourceCopy.nonPreapproved
}

const DynamicLenderDetailsBanner: React.FC<DynamicLenderDetailsBannerProps> = ({
  lenderDetails,
  source
}) => {
  if (!lenderDetails) return null

  const { preapproved, apr } = lenderDetails
  const aprValue = formatAprValue(apr)
  const headingText = getHeadingText(source, preapproved)

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.45), rgba(0,0,0,0.7)), url(${HERO_BG})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        p: { xs: '24px 16px', md: '50px 16px' },
        boxSizing: 'border-box'
      }}
      color={'common.white'}
      component="section"
      id="lender-details-banner"
    >
      <Container
        maxWidth="sm"
        sx={{
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center'
        }}
      >
        <Typography
          component="h1"
          variant="headlineLarge"
          sx={{
            fontWeight: 700,
            mb: { xs: 6, md: 7 }
          }}
          color={'common.white'}
        >
          {headingText}
        </Typography>

        {preapproved && <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            backgroundColor: '#fff',
            borderRadius: '32px',
            p: { xs: '12px 14px', sm: '14px 20px' },
            boxShadow: '0 18px 40px rgba(15,23,42,0.3)',
            mt: { xs: 3, md: 5 },
            width: '100%',
            maxWidth: 600,
            boxSizing: 'border-box'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              flex: 1,
              justifyContent: { xs: 'center', md: 'flex-start' }
            }}
          >
            <Box
              sx={{
                width: '28px',
                height: '28px',
                borderRadius: '32px',
                backgroundColor: preapproved ? 'rgba(22,163,74,0.12)' : 'rgba(37,99,235,0.08)',
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mr: 4
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: '18px', color: formlessColors.iconGreen }} />
            </Box>

            <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                Pre-approved with
                {renderLenderInline(lenderDetails)}
              </Typography>
          </Box>

          {aprValue && preapproved && (
            <Box
              sx={{
                ml: { xs: 0, sm: 7 },
                pl: { xs: 0, sm: 7 },
                borderLeft: {
                  xs: 'none',
                  sm: '1px solid #E5E7EB'
                },
                textAlign: { xs: 'left' },
                flexShrink: 0,
                display: { xs: 'flex', md: 'block' },
                justifyContent: { xs: 'center' },
                alignItems: 'center',
                flexDirection: { xs: 'row', md: 'column' }
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontSize: '10px',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  mt: { xs: 1, md: 0 },
                  mr: { xs: 1, md: 0 }
                }}
              >
                APR
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontSize: '18px',
                  fontWeight: 700,
                  lineHeight: 1.1
                }}
              >
                {aprValue}
              </Typography>
            </Box>
          )}
        </Box>
      }
      </Container>
    </Box>
  )
}

export default DynamicLenderDetailsBanner
